import type { IResponseFilterSegment } from 'entities/Segment'
import type { IParamsFiltersList } from 'entities/Broadcast/api/types'
import { type IParamsSendFromSmartOption } from 'entities/SendFrom'
import { type IResponseMediaShort } from 'entities/Attachment'
import { type IWorkflowV1Node } from 'entities/Workflow/utils/transformWorkflowV1UISettingsToV2'
import { type IIntegrationKey } from 'entities/Integrations'

export enum WorkflowStatus {
  Active = 1,
  Inactive = 0,
}

export type IWorkflowUser = {
  id: number
  organization_id: number
}

export type IWorkflowUiSettingsV2 = {
  branchNames: Record<string, { choice: string; default: string }>
}

export interface IBaseWorkflowData {
  name: string
  description: string
  multiple_executions: boolean
  definition: {
    trigger: Omit<IWorkFlowContactEventStep, 'args' | 'type'> & {
      args?:
        | IWorkFlowContactEventStep['args']
        | {
            filters: IResponseFilterSegment[]
          }
    }
    steps: Record<string, IWorkFlowStep>
  }
  ui_settings?: {
    data: { children: IWorkflowV1Node[] | null } | null
    data_v2?: IWorkflowUiSettingsV2
  } | null
  settings: { enable_quiet_hours: boolean } | null
}

export interface IFullWorkflowData extends IBaseWorkflowData {
  id: string
  user: IWorkflowUser
  enabled: boolean
  is_invalid: boolean
  executions_count: number
  source_type: string
  created_at: string
  updated_at: string | null
}

export type IResponseGetWorkflowById = {
  payload: IFullWorkflowData
}

export type IPreviewWorkflowData = Omit<IFullWorkflowData, 'ui_settings' | 'definition'>

export interface IParamsWorkflowList {
  preview?: boolean
  source_type?: 'core' | 'chatbot'
  'filters[status]'?: WorkflowStatus
  'filters[name]'?: string | null
  'page[pointer]'?: number
  'page[size]'?: number
}

export interface IResponseWorkflowListMeta {
  pagination: {
    hasPages: boolean
    isFirst: boolean
    items: number
    total: number
    pointer: {
      previous: number | null
      next: number | null
      current: number
    }
  }
  status: number
}

export interface IResponseWorkflowList<
  Payload extends IFullWorkflowData | IPreviewWorkflowData = IFullWorkflowData
> {
  payload: Payload[]
  meta: IResponseWorkflowListMeta
}

export interface IResponseWorkflowStatus {
  status: number
}

export interface IResponseWorkflowCreate {
  payload: IFullWorkflowData
  meta: IResponseWorkflowStatus
}

export interface IResponseWorkflowUpdate {
  payload: IFullWorkflowData
  meta: IResponseWorkflowStatus
}

export interface IResponseWorkflowEnable {
  payload: true
  meta: IResponseWorkflowStatus
}

export interface IResponseWorkflowDisable {
  payload: true
  meta: IResponseWorkflowStatus
}

export interface IResponseWorkflowDelete {
  payload: true
  meta: IResponseWorkflowStatus
}

export interface IParamsEnrollIntoWorkflow {
  contact_ids?: number[]
  apply_trigger_filters: boolean
  contact_filters?: IResponseFilterSegment[]
  search?: string
  apply_to_all?: boolean
}

export interface IResponseEnrollCheck {
  status: string
}

export type IWorkflowExecutionState =
  | 'created'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'terminated'
  | 'crashed'

export type IWorkflowSourceType = 'core' | 'chatbot'

export type IResponseWorkflowExecutionItem = {
  id: string
  state: IWorkflowExecutionState
  conversation_ids: number[]
  workflow_id: string
  workflow_name: string
  workflow_source_type: IWorkflowSourceType
  is_pause_supported: boolean
  paused_at: string | null
  pause_options: {
    timeout: number
  }
}

export interface IWorkflowExecutionPageRequest {
  size: number
  pointer: number
}

export interface IWorkflowPaginationPointer {
  previous: number | null
  next: number | null
  current: number
}

export interface IWorkflowPagination {
  hasPages: boolean
  isFirst: boolean
  items: number
  total: number
  pointer: IWorkflowPaginationPointer
}

export interface IWorkflowExecutionRequest {
  page: IWorkflowExecutionPageRequest
  state?: IWorkflowExecutionState
  workflow_source_type?: IWorkflowSourceType
}

export interface IResponseWorkflowExecution {
  payload: IResponseWorkflowExecutionItem[]
  meta: {
    status: number
    pagination: IWorkflowPagination
  }
}

export interface IResponseWorkflowTerminateExecution {
  payload: IResponseWorkflowExecutionItem
  meta: {
    status: number
  }
}

export type IWorkFlowType = IWorkFlowStep['type']
export type IWorkFlowActionType = IWorkFlowStep['id']

export type IWorkFlowContactEventStep = {
  id: 'contactEvent'
  args?: { filters: IParamsFiltersList }
  type?: 'trigger'
  next?: string
}

export type IWorkFlowTagsStep = {
  id: 'addTags' | 'removeTags'
  type: 'action'
  args?: IIWorkFlowTagsBlockArgs
  next?: string
}
export type IWorkFlowSendSMSStep = {
  id: 'sendMessage'
  type: 'action'
  args?: {
    body: string | null
    mediaUrls?: IResponseMediaShort[]
  } & IIWorkFlowSendFromBlockArgs

  next?: string
}

export type IWorkFlowSendRCSStep = {
  id: 'sendRCS'
  type: 'action'
  args?: {
    body: string | null
    mediaUrls?: IResponseMediaShort[]
  } & IIWorkFlowSendFromBlockArgs

  next?: string
}

export type IWorkFlowChoiceControl = {
  id: 'choice'
  type: 'control'
  args?: {
    // Will be added later
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    choices: any
    default: string
  }
  next?: string
}

export type IWorkFlowTimeControl = {
  id: 'awaitReply'
  type: 'control'
  args?: {
    timeout: string
    onInput: string
    onTimeout: string
  }
  next?: string
}
export type IWorkFlowDelayControl = {
  id: 'sleep'
  type: 'control'
  args?: {
    duration: string
    onWakeup: string
  }
  next?: string
}
export type IWorkFlowIfElseControl = {
  id: 'choice'
  type: 'control'
  args?: {
    choices: Record<string, IResponseFilterSegment[]>
    default: string
  }
  next?: string
}

export type IIWorkFlowTagsBlockArgs = {
  ids: number[]
}

export type IIWorkFlowSendFromBlockArgs = {
  inboxId: number | null
  sendingOptions?: {
    smart_option?: IParamsSendFromSmartOption
    number_id?: number | null
    send_from?: string
    teamId?: number | null
  }
}

export type IWorkFlowSendRVMStep = {
  id: 'sendRVM'
  type: 'action'
  args?: {
    audioUrl: string
  } & IIWorkFlowSendFromBlockArgs

  next?: string
}

export type IWorkFlowAddNoteStep = {
  id: 'addNote'
  type: 'action'
  args?: {
    note: string
  }
  next?: string
}

export type IWorkFlowAiAgentStep = {
  id: 'aiAgent'
  type: 'action'
  args?: {
    agentId: number | null
    applyFilters: boolean
  } & IIWorkFlowSendFromBlockArgs

  next?: string
}

export type IWebhookKey = 'post' | 'get'

export type IWorkFlowWebhookStep = {
  id: 'webhook'
  type: 'action'
  args?: {
    url: string
    method: IWebhookKey
  }
  next?: string
}

export type IWorkFlowContactFieldStep = {
  id: 'externalCoreUpdateContactField'
  type: 'action'
  args?: {
    field: string
    value: string | Date | null
    overrideExistingValue: boolean
  }
  next?: string
}

export type IIWorkFlowIntegrationStepField =
  | {
      integration_field_key: string
      source_type: 'contact_field' | null
      value: string | null
    }
  | {
      integration_field_key: string
      source_type: 'number' | null
      value: number | null
    }
  | {
      integration_field_key: string
      source_type: 'text' | null
      value: string | null
    }
  | {
      integration_field_key: string
      source_type: 'boolean' | null
      value: boolean | null
    }
  | {
      integration_field_key: string
      source_type: 'multi_select' | null
      value: Array<string | number> | null
    }
  | {
      integration_field_key: string
      source_type: 'array_of_strings' | null
      value: Array<string> | null
    }

export type IWorkFlowIntegrationStep = {
  id: 'externalMicroIntegrationAmpersandWrite' | 'externalMicroIntegrationNangoWrite'
  type: 'action'
  args: {
    integrationKey: IIntegrationKey
    objectName: string
    fieldsMapping?: Array<IIWorkFlowIntegrationStepField>
  }

  next?: string
}

export type IWorkFlowSuccessStep = {
  id: 'success'
  type: 'result'
  args?: null
  next?: string
}

export type IWorkFlowStep =
  | IWorkFlowContactEventStep
  | IWorkFlowTagsStep
  | IWorkFlowSendSMSStep
  | IWorkFlowSendRCSStep
  | IWorkFlowSendRVMStep
  | IWorkFlowAddNoteStep
  | IWorkFlowSuccessStep
  | IWorkFlowChoiceControl
  | IWorkFlowDelayControl
  | IWorkFlowTimeControl
  | IWorkFlowIfElseControl
  | IWorkFlowAiAgentStep
  | IWorkFlowIntegrationStep
  | IWorkFlowWebhookStep
  | IWorkFlowContactFieldStep

export type IResponseUpdateWorkflowsExecutionsList = IResponseWorkflowExecution

export type IParamsUpdateWorkflowsExecutionsList = {
  ids: string[]
}
